/**
 * Frontend date utilities for Chilean timezone handling
 * All dates displayed to users should be in Chilean time (America/Santiago)
 */

export const CHILEAN_TIMEZONE = 'America/Santiago';

/**
 * Format a date for display in Chilean timezone
 * Default format: "15 jul 2025"
 */
export const formatChileanDate = (
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: CHILEAN_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  };
  
  return dateObj.toLocaleDateString('es-CL', { ...defaultOptions, ...options });
};

/**
 * Format a date for display with long month names
 * Format: "15 de julio de 2025"
 */
export const formatChileanDateLong = (date: string | Date | null | undefined): string => {
  return formatChileanDate(date, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Format a date for display with short month names
 * Format: "15 jul 2025"
 */
export const formatChileanDateShort = (date: string | Date | null | undefined): string => {
  return formatChileanDate(date, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Format a date for display with minimal format
 * Format: "15/07"
 */
export const formatChileanDateMinimal = (date: string | Date | null | undefined): string => {
  return formatChileanDate(date, {
    day: '2-digit',
    month: '2-digit'
  });
};

/**
 * Format a date for display with compact format
 * Format: "29 Jun"
 */
export const formatChileanDateCompact = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('en-GB', {
    timeZone: CHILEAN_TIMEZONE,
    day: 'numeric',
    month: 'short'
  });
};

/**
 * Format a datetime for display in Chilean timezone
 * Format: "15/07/2025, 14:30"
 */
export const formatChileanDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleString('es-CL', {
    timeZone: CHILEAN_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format a time for display in Chilean timezone
 * Format: "14:30"
 */
/**
 * Date and clock time in Chile for "Actualizado en {date} a las {time}".
 * Date follows `locale` (es-CL → "1 de noviembre de 2026"). Time is 24h HH:MM.
 */
export const formatChileanMetricsUpdatedParts = (
  date: string | Date | null | undefined,
  locale = "es-CL",
): { date: string; time: string } | null => {
  if (!date) return null;

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return null;

  const language = locale.toLowerCase().startsWith("en") ? "en-US" : "es-CL";
  const dateLabel = dateObj.toLocaleDateString(language, {
    timeZone: CHILEAN_TIMEZONE,
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const timeParts = new Intl.DateTimeFormat("es-CL", {
    timeZone: CHILEAN_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(dateObj);
  const hour = timeParts.find((part) => part.type === "hour")?.value ?? "";
  const minute = timeParts.find((part) => part.type === "minute")?.value ?? "";
  if (!hour || !minute || !dateLabel) return null;

  return { date: dateLabel, time: `${hour}:${minute}` };
};

export const formatChileanTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleString('es-CL', {
    timeZone: CHILEAN_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get the current date in Chilean timezone
 * Returns Date object representing current Chilean time
 */
export const getCurrentChileanDate = (): Date => {
  const now = new Date();
  const chileanTime = new Date(now.toLocaleString('en-US', { timeZone: CHILEAN_TIMEZONE }));
  return chileanTime;
};

/**
 * Convert a date to Chilean timezone date string (YYYY-MM-DD)
 * Useful for API calls and date comparisons
 */
export const toChileanDateString = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('sv-SE', { 
    timeZone: CHILEAN_TIMEZONE 
  });
};

/**
 * Check if two dates are the same day in Chilean timezone
 */
export const isSameChileanDay = (date1: string | Date, date2: string | Date): boolean => {
  const day1 = toChileanDateString(date1);
  const day2 = toChileanDateString(date2);
  return day1 === day2;
};

/**
 * Get the start of day in Chilean timezone as UTC Date
 * Useful for date range queries
 */
export const getChileanDayStart = (date: string | Date): Date => {
  const dateStr = typeof date === 'string' ? date : toChileanDateString(date);
  if (!dateStr) return new Date();
  
  // Simple approach: create a date string that represents midnight in Chilean timezone
  // Then let JavaScript handle the timezone conversion
  const chileanMidnight = `${dateStr}T00:00:00.000`;
  
  // Create the target date and adjust for Chilean timezone
  const targetDate = new Date(chileanMidnight);
  const offsetMinutes = getChileanTimezoneOffset();
  
  return new Date(targetDate.getTime() - (offsetMinutes * 60000));
};

/**
 * Get the end of day in Chilean timezone as UTC Date
 * Useful for date range queries
 */
export const getChileanDayEnd = (date: string | Date): Date => {
  const dateStr = typeof date === 'string' ? date : toChileanDateString(date);
  if (!dateStr) return new Date();
  
  // Simple approach: create a date string that represents end of day in Chilean timezone
  const chileanEndOfDay = `${dateStr}T23:59:59.999`;
  
  // Create the target date and adjust for Chilean timezone
  const targetDate = new Date(chileanEndOfDay);
  const offsetMinutes = getChileanTimezoneOffset();
  
  return new Date(targetDate.getTime() - (offsetMinutes * 60000));
};

/**
 * Get Chilean timezone offset in minutes from UTC
 * Positive values mean Chilean time is ahead of UTC
 */
const getChileanTimezoneOffset = (): number => {
  const now = new Date();
  const utcTime = now.toLocaleString('en-US', { timeZone: 'UTC' });
  const chileanTime = now.toLocaleString('en-US', { timeZone: CHILEAN_TIMEZONE });
  
  const utcDate = new Date(utcTime);
  const chileanDate = new Date(chileanTime);
  
  return (utcDate.getTime() - chileanDate.getTime()) / (1000 * 60);
};

/**
 * Format date for chart x-axis labels
 * Returns short format suitable for charts
 */
function titleCaseWord(value: string): string {
  const trimmed = value.replace(/\.$/, "").trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Chart tooltip date with weekday first (e.g. "Miércoles 21 Oct").
 * YYYY-MM-DD is treated as a Chilean calendar day, not UTC midnight.
 */
export const formatChileanDateWithWeekday = (
  date: string | Date | null | undefined,
): string => {
  if (!date) return "";

  const chileanDay =
    typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)
      ? date.slice(0, 10)
      : toChileanDateString(date);
  if (!chileanDay) return "";

  const dateObj = new Date(`${chileanDay}T15:00:00.000Z`);
  if (isNaN(dateObj.getTime())) return "";

  const weekday = titleCaseWord(
    dateObj.toLocaleDateString("es-CL", {
      timeZone: CHILEAN_TIMEZONE,
      weekday: "long",
    }),
  );
  const day = dateObj.toLocaleDateString("es-CL", {
    timeZone: CHILEAN_TIMEZONE,
    day: "numeric",
  });
  const month = titleCaseWord(
    dateObj.toLocaleDateString("es-CL", {
      timeZone: CHILEAN_TIMEZONE,
      month: "short",
    }),
  );
  return weekday && day && month ? `${weekday} ${day} ${month}` : "";
};

export const formatChileanDateForChart = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('es-CL', {
    timeZone: CHILEAN_TIMEZONE,
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Parse a Chilean date string (DD/MM/YYYY) to Date object
 */
export const parseChileanDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  // Handle DD/MM/YYYY format
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Handle ISO format
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};
